import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FollowApi } from 'app/modules/dashboard/api/follow.api';
import { BehaviorSubject, Subscription, map, take } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { SkeletonModule } from 'primeng/skeleton';
import {
  NgStyle,
  NgClass,
  NgIf,
  NgFor,
  AsyncPipe,
  LowerCasePipe,
} from '@angular/common';
import { environment } from 'src/environments/environment.local';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Author, IconComponent, User } from '@kinomap-dev/ng-ui-lib';

@Component({
  selector: 'app-watch-like-list',
  templateUrl: './watchLikeList.component.html',
  styleUrls: ['./watchLikeList.component.scss'],
  standalone: true,
  imports: [
    NgStyle,
    NgClass,
    NgIf,
    NgFor,
    SkeletonModule,
    InfiniteScrollDirective,
    AsyncPipe,
    LowerCasePipe,
    TranslateModule,
    IconComponent
  ],
})
export class WatchLikeList implements OnDestroy {
  @Input() scrollContainerSelector: string;
  @Input() title: string;
  @Input() likeCount: BehaviorSubject<number>;
  @Input() liked: BehaviorSubject<boolean>;
  @Input() userLikes: BehaviorSubject<User[]>;
  @Input() currentUser: User;
  @Input() type:
    | 'watch'
    | 'scenic'
    | 'coaching'
    | 'activity'
    | 'mapmode'
    | 'workout';
  @Input() videoId: number;
  @Input() sessionId: string | undefined;

  @Output() likeClick: EventEmitter<any> = new EventEmitter<void>();
  followed: boolean = false; //  TODO: Check is user is followed.
  followedUser: Author[] = [];
  subscriptions: Subscription[] = [];

  likeProcess: boolean = false;
  userLikedPage: number = 1;
  userLikedLastPage: number;

  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  // responsive variables
  xSmallScreen: boolean;

  constructor(
    private responsive: BreakpointObserver,
    private followApi: FollowApi,
    private http: HttpClient
  ) {
    this.likeCount = new BehaviorSubject<number>(10);
    this.liked = new BehaviorSubject<boolean>(false);
    this.userLikes = new BehaviorSubject<User[]>([]);
    this.type = 'workout';
  }

  likeIconClick() {
    this.likeClick.emit('like');

    // Attendre que le parent mette à jour la valeur de 'liked'
    // puis mettre à jour la liste des utilisateurs
    setTimeout(() => {
      const currentLiked = this.liked.getValue();
      // Filtrer d'abord l'utilisateur courant de la liste existante quoi qu'il arrive
      const filteredLikes = this.userLikes
        .getValue()
        .filter((user) => user.id !== this.currentUser.id);

      if (currentLiked) {
        // Si l'utilisateur a liké, l'ajouter en haut de la liste
        this.userLikes.next([this.currentUser, ...filteredLikes]);
      } else {
        // Si l'utilisateur a retiré son like, garder la liste filtrée
        this.userLikes.next(filteredLikes);
      }
    }, 0);
  }

  getFollowStatus(userId: number): boolean {
    return (
      this.followedUser.find((user: User) => {
        return user.id === userId;
      }) !== undefined
    );
  }

  followUser(userId: number) {
    this.followApi
      .followUser(userId + '')
      .pipe(take(1))
      .subscribe((res) => {
        const newFollowed = this.userLikes.getValue().find((user: User) => {
          return user.id === userId;
        });
        this.followedUser.push(newFollowed);
      });
  }

  getUsersLiked(): void {
    let url: string = '';
    const params: HttpParams = new HttpParams().set('page', this.userLikedPage);

    switch (this.type) {
      case 'watch':
      case 'scenic':
        url = environment.api.dashboard.likeVideo.replace(
          '{video}',
          this.videoId.toString()
        );
        break;
      case 'coaching':
        url = environment.api.coaching.like.replace(
          '{coachingId}',
          this.videoId.toString()
        );
        break;
      case 'activity':
        url = environment.api.dashboard.likeActivity.replace(
          '{activity}',
          this.videoId.toString()
        );
        break;
      case 'mapmode':
        url = environment.api.gpsOnly.likes.replace(
          '{gpsOnlyId}',
          this.videoId.toString()
        );
        break;
      case 'workout':
        url = environment.api.workout.likes.replace(
          '{workoutId}',
          this.videoId.toString()
        );
        break;

      default:
        console.error('TYPE IS NOT CORRECT', this.type);
    }

    this.subscriptions.push(
      this.http.get(url, { params }).subscribe((res: any) => {
        const newUsers = res.data.filter(
          (user) => user.id !== this.currentUser.id
        );
        const existingUsers = this.userLikes
          .getValue()
          .filter((user) => user.id !== this.currentUser.id);
        const allUsers = [...existingUsers, ...newUsers];
        const followedUsers = allUsers.filter((user) =>
          this.getFollowStatus(user.id)
        );
        const notFollowedUsers = allUsers.filter(
          (user) => !this.getFollowStatus(user.id)
        );

        let sortedUsers: User[];

        if (this.liked.getValue()) {
          sortedUsers = [
            this.currentUser,
            ...followedUsers,
            ...notFollowedUsers,
          ];
        } else {
          sortedUsers = [...followedUsers, ...notFollowedUsers];
        }

        const userIds = new Set<number>();
        const uniqueUsers = sortedUsers.filter((user) => {
          if (userIds.has(user.id)) {
            return false;
          }
          userIds.add(user.id);
          return true;
        });

        this.userLikes.next(uniqueUsers);
        this.userLikedPage += 1;
        this.loading$.next(false);
      })
    );
  }

  onScrollBottom() {
    this.getUsersLiked();
  }

  ngAfterViewInit(): void {
    this.subscriptions = [
      // RESPONSIVE SUBSCRIPTIONS :
      this.responsive.observe(Breakpoints.XSmall).subscribe((result) => {
        this.xSmallScreen = result.matches ? true : false;
      }),
      this.followApi
        .getMyFollowings(1)
        .pipe(take(1))
        .subscribe((res: { data: Author[] }) => {
          this.followedUser = res.data;
          this.getUsersLiked();
        }),
    ];

    this.getUsersLiked();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
